import { type ChangeEvent, type DragEvent, useEffect, useRef, useState } from 'react';

import { Col, Row } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Link } from '@opendoor/atoms/link';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';
import {
  IconCircleCheckFilled,
  IconCircleDashed,
  IconCloudUpload,
  IconFile,
  IconLoader2,
} from '@tabler/icons-react';
import {
  documentFor,
  type OfferPortalWizardContext,
  requiredDocumentPurposes,
} from 'state_machines/OfferPortalWizardMachine';

import { type ListingDetails, listingPacketUrl } from './listingDetails';
import { uploadOfferDocument } from './offerDocumentUpload';
import { type OfferDocumentPurpose, type OfferDocumentRef } from './offerDraft';
import { MAX_OFFER_DOCUMENTS } from './offerSubmission';
import {
  CardHeading,
  DOCUMENT_ROW,
  FIELD_CARD,
  FieldError,
  formatFileSize,
  type Send,
} from './wizardFields';

const REQUIRED_DOCUMENT_LABELS: Record<
  ReturnType<typeof requiredDocumentPurposes>[number],
  string
> = {
  PURCHASE_AGREEMENT: 'Purchase agreement',
  ABA_DISCLOSURE: 'Affiliated Business Arrangement',
  OPENDOOR_ADDENDUM: 'Opendoor Addendum',
  PROOF_OF_FUNDS: 'Proof of funds',
  PRE_APPROVAL_LETTER: 'Mortgage pre-approval',
};

const FILE_RULES = 'PDF up to 10MB';
const ACCEPTED_FILE_TYPES = 'application/pdf';

interface PendingUpload {
  localId: number;
  purpose: OfferDocumentPurpose;
  fileName: string;
  sizeBytes: number;
}

/** One required document: empty, uploading, or filled. */
const RequiredDocumentRow = ({
  purpose,
  label,
  document,
  pending,
  onPick,
  send,
}: {
  purpose: OfferDocumentPurpose;
  label: string;
  document: OfferDocumentRef | undefined;
  pending: PendingUpload | undefined;
  onPick: (purpose: OfferDocumentPurpose) => void;
  send: Send;
}) => {
  const handlePick = () => {
    onPick(purpose);
  };
  const handleRemove = () => {
    if (document != null) {
      send({ type: 'REMOVE_DOCUMENT', documentId: document.documentId });
    }
  };
  const slotState = (): 'filled' | 'uploading' | 'empty' => {
    if (document != null) return 'filled';
    return pending != null ? 'uploading' : 'empty';
  };
  const state = slotState();

  const icon = () => {
    if (state === 'filled') {
      return <IconCircleCheckFilled className="size-8 shrink-0 text-od-content-success" />;
    }
    if (state === 'uploading') {
      return (
        <IconLoader2
          aria-label={`Uploading ${label}`}
          className="size-8 shrink-0 animate-spin text-od-content-tertiary"
        />
      );
    }
    return <IconCircleDashed className="size-8 shrink-0 text-od-content-tertiary" />;
  };

  const subtitle = () => {
    if (document != null) {
      return `${document.fileName} · ${formatFileSize(document.sizeBytes)}`;
    }
    return state === 'uploading' ? 'Uploading…' : FILE_RULES;
  };

  const action = () => {
    if (state === 'filled') {
      return (
        <Button
          type="button"
          variant="secondary"
          onClick={handleRemove}
          className="h-auto border-0 bg-transparent p-0 text-label-sm !text-od-content-primary underline not-disabled:hover:bg-transparent"
          aria-label={`Remove ${label}`}
          analyticsName="cosmos-make-offer-remove-document"
        >
          Remove
        </Button>
      );
    }
    if (state === 'uploading') {
      return null;
    }
    return (
      <Button
        type="button"
        variant="secondary"
        size="md"
        onClick={handlePick}
        // The row is a compact tile; the default large button dwarfs it, and
        // its width would squeeze the document name into an ellipsis.
        className="shrink-0 rounded-[12px]"
        aria-label={`Upload ${label}`}
        analyticsName="cosmos-make-offer-upload-document"
      >
        Upload
      </Button>
    );
  };

  return (
    <Row className={DOCUMENT_ROW}>
      {icon()}
      <Col className="min-w-0 flex-1 gap-0.5">
        <Text className="truncate text-label-sm font-medium text-od-content-primary">{label}</Text>
        <Text className="truncate text-body-sm text-od-content-secondary">{subtitle()}</Text>
      </Col>
      {action()}
    </Row>
  );
};

/** One extra attachment; `document` absent means the upload is in flight. */
const ExtraDocumentRow = ({
  fileName,
  sizeBytes,
  document,
  send,
}: {
  fileName: string;
  sizeBytes: number;
  document: OfferDocumentRef | undefined;
  send: Send;
}) => {
  const handleRemove = () => {
    if (document != null) {
      send({ type: 'REMOVE_DOCUMENT', documentId: document.documentId });
    }
  };
  return (
    <Row className={DOCUMENT_ROW}>
      <IconFile className="size-6 shrink-0 text-od-content-primary" />
      <Col className="min-w-0 flex-1 gap-0.5">
        <Text className="truncate text-label-sm font-medium text-od-content-primary">
          {fileName}
        </Text>
        <Text className="text-body-sm text-od-content-secondary">
          {document != null ? formatFileSize(sizeBytes) : 'Uploading…'}
        </Text>
      </Col>
      {document != null ? (
        <Button
          type="button"
          variant="secondary"
          onClick={handleRemove}
          className="h-auto border-0 bg-transparent p-0 text-label-sm !text-od-content-primary underline not-disabled:hover:bg-transparent"
          aria-label={`Remove ${fileName}`}
          analyticsName="cosmos-make-offer-remove-document"
        >
          Remove
        </Button>
      ) : (
        <IconLoader2
          aria-label="Uploading"
          className="size-5 animate-spin text-od-content-secondary"
        />
      )}
    </Row>
  );
};

/** Step 5 of 6 of the offer wizard — required documents and extras. */
const DocumentsStep = ({
  listing,
  context,
  send,
}: {
  listing: ListingDetails;
  context: OfferPortalWizardContext;
  send: Send;
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Which slot the hidden input is currently picking for.
  const pickingForRef = useRef<OfferDocumentPurpose>('OTHER');
  const nextLocalId = useRef(0);
  // The queue runs uploads one at a time so the first response's
  // submissionId groups every later file under the same S3 prefix — parallel
  // first uploads would each mint their own group.
  const submissionIdRef = useRef<string | null>(context.documentsSubmissionId);
  const uploadQueueRef = useRef<Promise<void>>(Promise.resolve());
  const [pendingUploads, setPendingUploads] = useState<PendingUpload[]>([]);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const requiredPurposes = requiredDocumentPurposes(context.financingType);

  const startUpload = (file: File, purpose: OfferDocumentPurpose) => {
    const localId = nextLocalId.current++;
    setUploadError(null);
    setPendingUploads((uploads) => [
      ...uploads,
      { localId, purpose, fileName: file.name, sizeBytes: file.size },
    ]);
    send({ type: 'UPLOAD_STARTED' });
    uploadQueueRef.current = uploadQueueRef.current.then(async () => {
      const result = await uploadOfferDocument({
        file,
        listingId: listing.id,
        purpose,
        submissionId: submissionIdRef.current,
      });
      setPendingUploads((uploads) => uploads.filter((upload) => upload.localId !== localId));
      send({ type: 'UPLOAD_SETTLED' });
      switch (result.kind) {
        case 'uploaded': {
          submissionIdRef.current = result.submissionId;
          send({ type: 'SET_DOCUMENTS_SUBMISSION_ID', value: result.submissionId });
          send({ type: 'ADD_DOCUMENT', document: result.document });
          return;
        }
        case 'rejected': {
          setUploadError(result.message);
          return;
        }
        default: {
          setUploadError(`${file.name} did not upload. Try again.`);
        }
      }
    });
  };

  const startUploads = (files: FileList | null, purpose: OfferDocumentPurpose) => {
    // Intake accepts at most MAX_OFFER_DOCUMENTS documents per offer; a file
    // past the cap stops here instead of landing in a draft the submit
    // schema rejects.
    //
    // Extras never spend the last slots a still-empty required purpose needs.
    // Without that reservation, enough additional documents would leave the
    // agent unable to attach a required one and unable to continue, with the
    // cap message giving no hint that removing an extra is the way out.
    const attached = [...context.documents, ...pendingUploads];
    const unfilledRequired = requiredPurposes.filter(
      (required) => !attached.some((document) => document.purpose === required),
    ).length;
    const reserved = purpose === 'OTHER' ? unfilledRequired : 0;
    let capacity = MAX_OFFER_DOCUMENTS - attached.length - reserved;
    for (const file of Array.from(files ?? [])) {
      if (capacity <= 0) {
        setUploadError(
          reserved > 0
            ? 'Attach the required documents first, or remove one you have added.'
            : `You can attach up to ${MAX_OFFER_DOCUMENTS} documents.`,
        );
        return;
      }
      capacity -= 1;
      startUpload(file, purpose);
      // A required slot takes one file, however many were selected.
      if (purpose !== 'OTHER') {
        return;
      }
    }
  };

  const handleFileInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    startUploads(event.target.files, pickingForRef.current);
    // The same file must be pickable again after a failure.
    event.target.value = '';
  };

  const pickFor = (purpose: OfferDocumentPurpose) => {
    pickingForRef.current = purpose;
    fileInputRef.current?.click();
  };

  // A file released anywhere but the drop zone is the browser's to handle, and
  // its default is to open that file in this tab — which unloads the wizard
  // mid-offer. Swallowing the page-level drag events keeps a near miss inert.
  useEffect(() => {
    const swallow = (event: Event) => {
      event.preventDefault();
    };
    window.addEventListener('dragover', swallow);
    window.addEventListener('drop', swallow);
    return () => {
      window.removeEventListener('dragover', swallow);
      window.removeEventListener('drop', swallow);
    };
  }, []);

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragActive(false);
    startUploads(event.dataTransfer.files, 'OTHER');
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
    // dragleave also fires when the cursor moves onto the zone's children.
    if (event.relatedTarget instanceof Node && event.currentTarget.contains(event.relatedTarget)) {
      return;
    }
    setDragActive(false);
  };

  const handleExtraZoneClick = () => {
    pickFor('OTHER');
  };

  const documentsViolation = context.fieldViolations['documents'];
  const uploadsInFlight = context.documentUploadsInFlight > 0;
  const packetUrl = listingPacketUrl(listing);
  const extras = context.documents.filter((document) => document.purpose === 'OTHER');
  const pendingExtras = pendingUploads.filter((upload) => upload.purpose === 'OTHER');
  const missingRequired = requiredPurposes.filter(
    (purpose) => documentFor(context, purpose) == null,
  );

  return (
    <>
      <Col className={FIELD_CARD}>
        <CardHeading
          title="Required documents"
          caption={
            <>
              Upload each of the required documents below.{' '}
              {packetUrl != null ? (
                // The Rails path answers with a 302 to a fresh presigned S3 GET
                // on every click, so the link never carries a stale URL.
                <Link
                  href={packetUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-body-sm text-od-content-secondary underline"
                  analyticsName="cosmos-make-offer-see-templates"
                >
                  See templates
                </Link>
              ) : null}
            </>
          }
        />
        <Col className="gap-2">
          {requiredPurposes.map((purpose) => (
            <RequiredDocumentRow
              key={purpose}
              purpose={purpose}
              label={REQUIRED_DOCUMENT_LABELS[purpose]}
              document={documentFor(context, purpose)}
              pending={pendingUploads.find((upload) => upload.purpose === purpose)}
              onPick={pickFor}
              send={send}
            />
          ))}
        </Col>
        {uploadError != null ? <FieldError>{uploadError}</FieldError> : null}
        {documentsViolation != null ? <FieldError>{documentsViolation}</FieldError> : null}
        {context.showError && uploadsInFlight ? (
          <FieldError>Wait for the uploads to finish.</FieldError>
        ) : null}
        {context.showError && !uploadsInFlight && missingRequired.length > 0 ? (
          <FieldError>
            Upload the{' '}
            {missingRequired.map((purpose) => REQUIRED_DOCUMENT_LABELS[purpose]).join(', ')} to
            continue.
          </FieldError>
        ) : null}
      </Col>
      <Col className={FIELD_CARD}>
        <CardHeading
          title="Additional documents"
          caption="Attach any additional documents that are important, like a signed packet."
        />
        {extras.length > 0 || pendingExtras.length > 0 ? (
          <Col className="gap-2">
            {extras.map((document) => (
              <ExtraDocumentRow
                key={document.documentId}
                fileName={document.fileName}
                sizeBytes={document.sizeBytes}
                document={document}
                send={send}
              />
            ))}
            {pendingExtras.map((upload) => (
              <ExtraDocumentRow
                key={upload.localId}
                fileName={upload.fileName}
                sizeBytes={upload.sizeBytes}
                document={undefined}
                send={send}
              />
            ))}
          </Col>
        ) : null}
        <Col
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          className={cn(
            'items-center gap-4 rounded-[12px] border border-dashed py-8',
            dragActive
              ? 'border-od-border-accent-brand bg-od-bg-accent-brand/10'
              : 'border-od-border-primary bg-od-bg-secondary',
          )}
        >
          <IconCloudUpload className="size-6 text-od-content-accent-brand" />
          <Col className="items-center gap-1">
            <Button
              type="button"
              variant="secondary"
              onClick={handleExtraZoneClick}
              className="h-auto border-0 bg-transparent p-0 text-label-sm font-medium underline not-disabled:hover:bg-transparent"
              aria-label="Drop files here"
              analyticsName="cosmos-make-offer-upload-documents"
            >
              Drop files here
            </Button>
            <Text className="text-body-sm text-od-content-tertiary">{FILE_RULES}</Text>
          </Col>
        </Col>
      </Col>
      <input
        ref={fileInputRef}
        type="file"
        hidden
        multiple
        accept={ACCEPTED_FILE_TYPES}
        onChange={handleFileInputChange}
        aria-label="Upload documents"
      />
    </>
  );
};

export default DocumentsStep;
